import { AgxAvatar, AgxSelectChip, AgxSpacer, SelectChipOption } from '@urbanx/agx.ui-components2';
import TasksIcon from 'components/sidebar/icons/tasks-icon';
import { ContentLayout, PageLayout } from 'layout';
import { isEqual } from 'lodash-es';
import { useGetAssigneeOptions } from 'pages/allTasks/TaskFilters/useGetAssigneeOptions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import TodaysTaskStats from './Components/TodaysTaskStats/TodaysTaskStats';
import TodaysTasks from './Components/TodaysTasks/TodaysTasks';
import { DashboardStats } from './Components/DashboardStats/DashboardStats';
import { useGetDashboardStats } from '@urbanx/agx.activities.client';
import { useTaskCollectionChangeNotifier } from 'pages/allTasks/useTaskCollectionChangeNotifier';
import { TasksSearchFilterType } from './Components/TasksModal/TasksSearchFilterType';
import { useAgencyFilter } from 'hooks/useAgencyFilter';
import TasksModal from './Components/TasksModal/TasksModal';
import {
  createInitialDashboardFilterState,
  storeDashboardFilterState,
} from './tasksDashboardFilters';

const initialDashboardFilterState = createInitialDashboardFilterState();

const TasksDashboardPage = () => {
  useTaskCollectionChangeNotifier();
  const [assignedToUserIds, setAssignedToUserIds] = useState<string[] | undefined>(
    initialDashboardFilterState.assignedToUserIds
  );
  const [selectedAgencies, setSelectedAgencies] = useState<string[] | undefined>(
    initialDashboardFilterState.agencyIds
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchFilterType, setSearchFilterType] = useState<TasksSearchFilterType>(
    TasksSearchFilterType.Launches
  );

  const { assigneeOptions, assigneeOptionsPending } = useGetAssigneeOptions(assignedToUserIds);

  useEffect(() => {
    storeDashboardFilterState({
      agencyIds: selectedAgencies,
      assignedToUserIds: assignedToUserIds,
    });
  }, [assignedToUserIds, selectedAgencies]);

  useEffect(() => {
    if (assigneeOptionsPending) return;
    if (!assignedToUserIds) return;
    if (assigneeOptions.length <= 1) return;
    if (assignedToUserIds.every((id) => assigneeOptions.find((opt) => opt.value === id))) return;

    const filteredAssignedToUserIds = assignedToUserIds.filter((id) =>
      assigneeOptions.find((opt) => opt.value === id)
    );
    setAssignedToUserIds(filteredAssignedToUserIds);
  }, [assigneeOptions, assignedToUserIds, assigneeOptionsPending]);

  const {
    agencyOptions,
    effectiveAgencyIds,
    agencies,
    isPending: isAgencyFilterPending,
    isAgencyScopeUnavailable,
    hasNoMatchingAgencies,
  } = useAgencyFilter({ selectedValues: selectedAgencies });

  const handleAssigneeFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedAssigneeValue = selected.map((option) => option.value as string);

      if (!isEqual(selectedAssigneeValue, assignedToUserIds)) {
        setAssignedToUserIds(selectedAssigneeValue);
      }
    },
    [assignedToUserIds]
  );

  const handleAgencyFilterUpdate = useCallback(
    (selected: SelectChipOption[] = []) => {
      const selectedAgencyValue = selected.map((option) => option.value as string);

      if (!isEqual(selectedAgencyValue, selectedAgencies)) setSelectedAgencies(selectedAgencyValue);
    },
    [selectedAgencies]
  );

  const loading = useMemo(
    () => assigneeOptionsPending || isAgencyFilterPending,
    [assigneeOptionsPending, isAgencyFilterPending]
  );
  const filterUnavailable = loading || isAgencyScopeUnavailable || hasNoMatchingAgencies;

  const { data: dashboardStats } = useGetDashboardStats(
    effectiveAgencyIds,
    assignedToUserIds,
    !filterUnavailable
  );

  const onSearchFilterTypeUpdated = useCallback((filterType: TasksSearchFilterType) => {
    setSearchFilterType(filterType);
    setIsModalOpen(true);
  }, []);

  return (
    <PageLayout
      primaryPageTitle="Dashboard"
      agentName={''}
      pageIcon={
        <AgxAvatar size="lg" type="icon-dark" icon={<TasksIcon width={28} height={28} />} />
      }
      header={
        <AgxSpacer
          direction="horizontal"
          contentAlignment="center"
          contentJustification="space-between"
          fill
          style={{
            borderRadius: 'var(--radius-6xl, 40px)',
            background: 'var(--bg-secondary, #FFF)',
          }}
          padding="md"
        >
          <DashboardStats
            tasksDueToday={dashboardStats?.numberOfIncompleteTasksDueToday}
            tasksOverdue={dashboardStats?.numberOfOverdueTasks}
          />
          <AgxSpacer
            direction="horizontal"
            contentAlignment="center"
            gap="sm"
            contentJustification="end"
          >
            <AgxSelectChip
              id={'filterAssignee'}
              variant="chip"
              label={'Assignee'}
              onSelect={handleAssigneeFilterUpdate}
              onReset={handleAssigneeFilterUpdate}
              size={'medium'}
              type={'multi-select'}
              options={assigneeOptions}
              searchPlaceHolder="Search Assignee"
              maxLabels={0}
            />
            <AgxSelectChip
              id={'filterAgency'}
              variant="chip"
              label={'Agency'}
              onSelect={handleAgencyFilterUpdate}
              onReset={handleAgencyFilterUpdate}
              size={'medium'}
              type={'multi-select'}
              align="right"
              searchPlaceHolder="Search agency"
              options={agencyOptions}
              maxLabels={0}
            />
          </AgxSpacer>
        </AgxSpacer>
      }
    >
      <ContentLayout hideBackground>
        <AgxSpacer direction="vertical" gap="lg" fill>
          <TodaysTaskStats
            dashboardStats={filterUnavailable ? undefined : dashboardStats}
            onStatTypeSelected={onSearchFilterTypeUpdated}
          />
          <TodaysTasks
            isLoading={loading}
            filtersPending={filterUnavailable}
            todaysTasksCount={
              filterUnavailable ? 0 : (dashboardStats?.numberOfIncompleteTasksDueToday ?? 0)
            }
            selectedAgencies={effectiveAgencyIds}
            assignedToUserIds={assignedToUserIds}
            assigneeOptions={assigneeOptions}
            agencies={agencies}
            isAgencyOptionsPending={isAgencyFilterPending}
            assigneeOptionsPending={assigneeOptionsPending}
            hasNoMatchingAgencies={hasNoMatchingAgencies}
          />
        </AgxSpacer>
        <TasksModal
          assignedToUserIds={assignedToUserIds}
          selectedAgencies={effectiveAgencyIds}
          searchFilterType={searchFilterType}
          agencies={agencies}
          filtersPending={filterUnavailable}
          hasNoMatchingAgencies={hasNoMatchingAgencies}
          assigneeOptions={assigneeOptions}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
        />
      </ContentLayout>
    </PageLayout>
  );
};

export default TasksDashboardPage;
